import { useMemo } from 'react';
import { STAGES, fmtMoney, fmtDate, today, dgsCheck } from '../../utils/constants';

function daysSince(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return Math.floor((new Date() - d) / (1000 * 60 * 60 * 24));
}

function HealthDot({ color, label }) {
  const colors = { green: '#22c55e', yellow: '#f59e0b', red: '#ef4444', gray: '#9ca3af' };
  return (
    <span title={label} style={{
      display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
      background: colors[color] || colors.gray, marginRight: 4, flexShrink: 0,
    }} />
  );
}

function Badge({ children, color = 'slate' }) {
  const bg = { red: 'var(--red-50)', orange: 'var(--orange-50)', teal: 'var(--teal-50)', slate: 'var(--slate-100)', green: 'var(--green-50)' };
  const fg = { red: 'var(--red-700)', orange: 'var(--orange-700)', teal: 'var(--teal-700)', slate: 'var(--slate-500)', green: 'var(--green-700)' };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 99, background: bg[color], color: fg[color], whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

export default function SupervisorView({ contracts, paal, profile, onOpen }) {
  const active = useMemo(() => (contracts || []).filter(c => !c.archived), [contracts]);

  const rows = useMemo(() => active.map(c => {
    const dgs = dgsCheck(c, paal);
    const stageInfo = STAGES[c.stage];
    const daysInStage = daysSince(
      [...(c.stageHistory || [])].reverse().find(h => h.stage === c.stage)?.date
    );
    const dvbeComplete = c.stage >= 7 && c.dvbe?.solMethod && c.dvbe?.sbGoal && c.dvbe?.dvbeRequired;
    const dvbePending = c.stage === 7 && !dvbeComplete;

    // Health score
    let health = 'green';
    if (daysInStage > 30) health = 'red';
    else if (daysInStage > 14) health = 'yellow';
    if (dvbePending) health = 'yellow';
    if (dgs.required && c.stage >= 12) health = 'red';

    return { c, dgs, stageInfo, daysInStage, dvbeComplete, dvbePending, health };
  }), [active, paal]);

  // Summary stats
  const stats = useMemo(() => ({
    total: rows.length,
    attention: rows.filter(r => r.health !== 'green').length,
    dgsRequired: rows.filter(r => r.dgs.required).length,
    dvbePending: rows.filter(r => r.dvbePending).length,
    stalled: rows.filter(r => (r.daysInStage || 0) > 14).length,
  }), [rows]);

  const phaseOrder = ['pre', 'sol', 'award', 'exec', 'handoff'];
  const byPhase = useMemo(() => {
    const grouped = {};
    phaseOrder.forEach(p => { grouped[p] = []; });
    rows.forEach(r => {
      const phase = r.stageInfo?.phase || 'pre';
      if (!grouped[phase]) grouped[phase] = [];
      grouped[phase].push(r);
    });
    return grouped;
  }, [rows]);

  const phaseLabels = {
    pre: 'Pre-Solicitation', sol: 'Solicitation', award: 'Award',
    exec: 'Execution', handoff: 'Handoff',
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--navy-900)', marginBottom: 4 }}>Supervisor Dashboard</h1>
        <p style={{ fontSize: 13.5, color: 'var(--slate-500)' }}>
          Contract health across all active procurements · {today()}
        </p>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Total Active', value: stats.total, color: 'var(--navy-700)' },
          { label: 'Need Attention', value: stats.attention, color: stats.attention > 0 ? 'var(--red-600)' : 'var(--green-600)' },
          { label: 'DGS Required', value: stats.dgsRequired, color: stats.dgsRequired > 0 ? 'var(--orange-600)' : 'var(--slate-400)' },
          { label: 'SB/DVBE Pending', value: stats.dvbePending, color: stats.dvbePending > 0 ? 'var(--orange-600)' : 'var(--slate-400)' },
          { label: 'Stalled >14 Days', value: stats.stalled, color: stats.stalled > 0 ? 'var(--red-600)' : 'var(--slate-400)' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ background: 'var(--white)', border: '1px solid var(--slate-200)', borderRadius: 'var(--r-lg)', padding: '14px 16px' }}>
            <div style={{ fontSize: 11, color: 'var(--slate-400)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Contracts by phase */}
      {phaseOrder.map(phase => {
        const phaseRows = byPhase[phase] || [];
        if (phaseRows.length === 0) return null;
        return (
          <div key={phase} style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--slate-400)', marginBottom: 10 }}>
              {phaseLabels[phase]} · {phaseRows.length} contract{phaseRows.length !== 1 ? 's' : ''}
            </div>
            <div style={{ background: 'var(--white)', border: '1px solid var(--slate-200)', borderRadius: 'var(--r-lg)', overflow: 'hidden' }}>
              {/* Header */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 80px 55px 110px 160px 90px', gap: 12, padding: '10px 16px', background: 'var(--slate-50)', borderBottom: '1px solid var(--slate-100)', fontSize: 11, fontWeight: 700, color: 'var(--slate-400)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                <div>Contract</div>
                <div>Stage</div>
                <div>Budget</div>
                <div>Days</div>
                <div>Analyst</div>
                <div>DGS Status</div>
                <div>Flags</div>
              </div>
              {/* Rows */}
              {phaseRows.map(({ c, dgs, stageInfo, daysInStage, dvbePending, health }) => (
                <div
                  key={c.id}
                  onClick={() => onOpen(c.id)}
                  style={{
                    display: 'grid', gridTemplateColumns: '1fr 140px 80px 55px 110px 160px 90px',
                    gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--slate-100)',
                    cursor: 'pointer', alignItems: 'center', transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--slate-50)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <HealthDot color={health} label={health === 'green' ? 'On track' : health === 'yellow' ? 'Needs attention' : 'At risk'} />
                    <div>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--navy-800)', marginBottom: 2 }}>{c.title || 'Untitled'}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--slate-400)' }}>{c.procMethod || 'No method'} · {c.isIT === 'yes' ? 'IT' : 'Non-IT'}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--slate-600)' }}>{stageInfo?.label || 'Unknown'}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--slate-600)' }}>{fmtMoney(c.budget)}</div>
                  <div style={{ fontSize: 12.5, color: daysInStage > 14 ? 'var(--red-600)' : 'var(--slate-600)', fontWeight: daysInStage > 14 ? 700 : 400 }}>
                    {daysInStage !== null ? daysInStage + 'd' : '—'}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--slate-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.analystName || c.s215?.contactName || '—'}
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                    {dgs.otaRequired && dgs.olsRequired
                      ? <><Badge color="red">DGS/OTA</Badge><Badge color="orange">DGS/OLS</Badge></>
                      : dgs.olsRequired
                        ? <Badge color="orange">DGS/OLS</Badge>
                        : <Badge color="green">Within Auth</Badge>
                    }
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {dvbePending && <Badge color="orange">SB/DVBE</Badge>}
                    {daysInStage > 30 && <Badge color="red">Stalled</Badge>}
                    {c.stage >= 14 && !c.archived && <Badge color="teal">Handoff</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {rows.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 24px', color: 'var(--slate-400)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📊</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>No active contracts</div>
        </div>
      )}
    </div>
  );
}
